// windPressureCenters.ts — H/L pressure-center map labels from QA manifest.
//
// Spec: data/wind-validity-check-spec-2026-05-28.md
//
// Renders:
//   • 'wind-pressure-centers-label'  — bold H or L text (28 px, red/blue)
//   • 'wind-pressure-centers-hpa'    — pressure in hPa below the letter (12 px)
//
// Visibility: coupled to the Wind layer. NOT a separate user toggle.
// Data source: /api/wind-manifest  →  qa.lowCenters + qa.highCenters
// Zoom range: 3–7 (synoptic scale only)
// Popup: click → HTML popup with full detail (pressure, score, level, cycle)
//
// Extracted as a standalone module so the WindPressureCentersController
// (src/map/controllers/) can import it cleanly without touching MapView.

import type { Map as MlMap } from 'maplibre-gl';
import maplibre from 'maplibre-gl';

export const PRESSURE_SOURCE_ID = 'wind-pressure-centers';
export const PRESSURE_LABEL_LAYER_ID = 'wind-pressure-centers-label';
export const PRESSURE_HPA_LAYER_ID   = 'wind-pressure-centers-hpa';

export interface PressureCenter {
  lat: number;
  lon: number;
  pressureHpa: number;
  score: number;
  level: number;
}

export interface PressureCentersData {
  lowCenters:  PressureCenter[];
  highCenters: PressureCenter[];
  cycle: string;
  generatedAt: string;
  passed: boolean | null;
}

/** Build a GeoJSON FeatureCollection from QA low/high center arrays. */
export function buildPressureCentersFC(
  data: PressureCentersData,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];

  for (const c of data.lowCenters) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [c.lon, c.lat] },
      properties: {
        kind:        'low',
        label:       'L',
        pressureHpa: c.pressureHpa,
        pressureStr: String(Math.round(c.pressureHpa)),
        score:       c.score,
        level:       c.level,
        cycle:       data.cycle,
        generatedAt: data.generatedAt,
        qaPassed:    data.passed,
      },
    });
  }

  for (const c of data.highCenters) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [c.lon, c.lat] },
      properties: {
        kind:        'high',
        label:       'H',
        pressureHpa: c.pressureHpa,
        pressureStr: String(Math.round(c.pressureHpa)),
        score:       c.score,
        level:       c.level,
        cycle:       data.cycle,
        generatedAt: data.generatedAt,
        qaPassed:    data.passed,
      },
    });
  }

  return { type: 'FeatureCollection', features };
}

/** Mount pressure-center source + symbol layers. Idempotent. */
export function addWindPressureCentersLayers(map: MlMap): void {
  if (!map.getSource(PRESSURE_SOURCE_ID)) {
    map.addSource(PRESSURE_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }

  if (!map.getLayer(PRESSURE_LABEL_LAYER_ID)) {
    map.addLayer({
      id:     PRESSURE_LABEL_LAYER_ID,
      type:   'symbol',
      source: PRESSURE_SOURCE_ID,
      // Visible across synoptic AND regional zooms. Pressure centers are
      // O(1000 km) features but they still drive wind at sectional zoom, so
      // we want the H/L glyphs visible when boss is planning a regional leg.
      // Removed the old maxzoom: 7 cutoff that hid labels at zoom 8+ — the
      // exact zooms boss actually flies at (handoff 2026-05-28).
      minzoom: 3,
      layout: {
        visibility:   'none',
        'text-field':  ['get', 'label'],
        'text-font':   ['Noto Sans Regular'],
        // Shrink the glyph as we zoom in so it doesn't dominate the chart.
        'text-size':   ['interpolate', ['linear'], ['zoom'], 3, 28, 8, 24, 12, 18],
        'text-anchor': 'center',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': [
          'match', ['get', 'kind'],
          'low',  '#dc2626',
          'high', '#2563eb',
          '#ffffff',
        ],
        'text-halo-color': '#ffffff',
        'text-halo-width': 2,
      },
    });
  }

  if (!map.getLayer(PRESSURE_HPA_LAYER_ID)) {
    map.addLayer({
      id:     PRESSURE_HPA_LAYER_ID,
      type:   'symbol',
      source: PRESSURE_SOURCE_ID,
      minzoom: 3,
      layout: {
        visibility:   'none',
        'text-field':  ['get', 'pressureStr'],
        'text-font':   ['Noto Sans Regular'],
        'text-size':   ['interpolate', ['linear'], ['zoom'], 3, 12, 8, 11, 12, 10],
        'text-anchor': 'top',
        'text-offset': [0, 1.2],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': [
          'match', ['get', 'kind'],
          'low',  '#dc2626',
          'high', '#2563eb',
          '#ffffff',
        ],
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5,
      },
    });
  }
}

/** Show or hide both pressure-center layers at once. */
export function setPressureCentersVisibility(map: MlMap, visible: boolean): void {
  const v = visible ? 'visible' : 'none';
  try {
    if (map.getLayer(PRESSURE_LABEL_LAYER_ID)) {
      map.setLayoutProperty(PRESSURE_LABEL_LAYER_ID, 'visibility', v);
    }
    if (map.getLayer(PRESSURE_HPA_LAYER_ID)) {
      map.setLayoutProperty(PRESSURE_HPA_LAYER_ID, 'visibility', v);
    }
  } catch { /* map not ready */ }
}

/** Update the GeoJSON data for the pressure-center source. */
export function updatePressureCentersData(
  map: MlMap,
  fc: GeoJSON.FeatureCollection,
): void {
  try {
    const src = map.getSource(PRESSURE_SOURCE_ID) as
      | maplibre.GeoJSONSource
      | undefined;
    src?.setData(fc);
  } catch { /* source not yet mounted */ }
}

// ─── Click popup ──────────────────────────────────────────────────────────────

let _activePopup: maplibre.Popup | null = null;

function fmtCycle(cycleKey: string): string {
  const m = cycleKey.match(/^(\d{4})(\d{2})(\d{2})_(\d{2})$/);
  if (!m) return cycleKey;
  const [, , mm, dd, hh] = m;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${hh}Z ${Number(dd)}-${months[Number(mm) - 1] ?? mm}`;
}

function buildPopupHtml(props: Record<string, unknown>): string {
  const kind  = String(props.kind ?? '').toUpperCase();
  const color = kind === 'LOW' ? '#dc2626' : '#2563eb';
  return `
    <div style="font:12px/1.5 system-ui,sans-serif;min-width:160px;">
      <div style="font-weight:700;font-size:16px;color:${color};margin-bottom:4px;">
        ${kind} · ${props.pressureHpa ?? '—'} hPa
      </div>
      <div>Rotation score: <b>${
        props.score != null
          ? `${(Number(props.score) * 100).toFixed(0)}%`
          : '—'
      }</b></div>
      <div>Wind level: <b>${props.level ?? '—'} mb</b></div>
      <div>Cycle: <b>${fmtCycle(String(props.cycle ?? ''))}</b></div>
      <div style="margin-top:4px;font-size:10px;opacity:0.65;">
        ${props.generatedAt
          ? `Generated: ${new Date(String(props.generatedAt)).toUTCString()}`
          : ''}
      </div>
    </div>`;
}

/** Wire click-to-popup on the pressure-center layers. Returns cleanup fn. */
export function attachPressureCentersClickHandler(map: MlMap): () => void {
  const LAYERS = [PRESSURE_LABEL_LAYER_ID, PRESSURE_HPA_LAYER_ID];

  const onClick = (e: maplibre.MapMouseEvent & { features?: maplibre.MapGeoJSONFeature[] }) => {
    if (!e.features?.length) return;
    const props = (e.features[0].properties ?? {}) as Record<string, unknown>;
    if (_activePopup) { try { _activePopup.remove(); } catch { /* */ } }
    _activePopup = new maplibre.Popup({ closeButton: true, offset: 8 })
      .setLngLat(e.lngLat)
      .setHTML(buildPopupHtml(props))
      .addTo(map);
  };

  const onEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
  const onLeave = () => { map.getCanvas().style.cursor = '';        };

  for (const id of LAYERS) {
    map.on('click',       id, onClick as (e: maplibre.MapMouseEvent) => void);
    map.on('mouseenter',  id, onEnter);
    map.on('mouseleave',  id, onLeave);
  }

  return () => {
    for (const id of LAYERS) {
      map.off('click',      id, onClick as (e: maplibre.MapMouseEvent) => void);
      map.off('mouseenter', id, onEnter);
      map.off('mouseleave', id, onLeave);
    }
    if (_activePopup) { try { _activePopup.remove(); } catch { /* */ } _activePopup = null; }
  };
}
